import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogConfig } from '@angular/material/dialog';
import { ModalComponent as ModalComponent } from './modal.component';
import { ModalConfig } from './modal.interface';
import { TranslateService } from '../../utilities/translate/translate.service';
import { ModalPromptComponent } from './modals/prompt/prompt.component';
import { ModalTinyMCEComponent } from './modals/tinymce/tinymce.component';
import { ModalAssignToComponent } from './modals/assign-to/assign-to.component';
import { ProjectElement } from '../../resources/element/project-element.interface';
import { ElementAssignToOptions } from './modals/assign-to/assign-to.interface';
import { ModalFiltersComponent } from './modals/filters/filters.component';
import { FilterTreeJSON } from './modals/filters/filters.interface';
import { ModalParagraphAddComponent } from './modals/paragraph-add/paragraph-add.component';
import { ProjectSubmodule } from '../../resources/submodule/project-submodule.interface';
import { ModalRestoreParagraphComponent } from './modals/restore-paragraph/restore-paragraph.component';
import { AreaAddComponent } from '../../resources/area/area-add/area-add.component';
import { Building } from '../../resources/building/building.interface';
import { ClusterAddComponent } from '../../resources/cluster/cluster-add/cluster-add.component';
import { BasicArea } from '../../resources/basic-area/basic-area.interface';
import { Area } from '../../resources/area/area.interface';
import { ClusterEditComponent } from '../../resources/cluster/cluster-edit/cluster-edit.component';
import { Cluster } from '../../resources/cluster/cluster.interface';

/**
 * Modal confirm service.
 *
 * The modal is mostly a wrapper for [Material's MatDialog](https://material.angular.io/components/dialog/overview).
 */
@Injectable({
  providedIn: 'root'
})
export class ModalService {

  /** Default confirm modal configuration. */
  private DEFAULT_CONFIRM_CONFIG: Partial<ModalConfig> = {};


  /** Constructor. */
  constructor(
    private translateService: TranslateService,
    private matDialog: MatDialog
  ) {
    this.DEFAULT_CONFIRM_CONFIG = {
      type: 'primary',
      actions: [{
        action: 'confirm',
        label: this.translateService.get('submit')
      }]
    };
  }

  /**
   * Open alert modal (forced to make a choice).
   *
   * The modal is a wrapper for [Material's MatDialog](https://material.angular.io/components/dialog/overview).
   *
   * Shorthand for ModalConfirmService.open() with the correct config. Alert modals
   * cannot be closed by clicking the backdrop. By default, the cancel-button is
   * disabled too. To enable the cancel button, set `disableClose: false` in config.
   * The backdrop will still be disabled, to force the user to make a choice.
   *
   * - After opening, a MatDialogReference is returned, see documentation.
   * - After closing, an action is dispatched to `afterClosed()`:
   *  - *cancel*: When clicking the 'cancel'-button;
   *  - *action*: When the action button is clicked;
   *
   * *Note: All observable methods are automatically unsubscribed after one take.*
   *
   * @param config Modal configuration.
   * @returns Dialog reference as a MatDialogRef.
   */
  public alert(config: ModalConfig): MatDialogRef<ModalComponent> {
    const dialogConfig = {
      disableClose: true,
      data: {
        disableClose: true,
        ...this._parseActions(config)
      }
    } as MatDialogConfig;
    return this.matDialog.open(ModalComponent, dialogConfig);
  }

  /**
   * Open confirm modal.
   *
   * The modal is a wrapper for [Material's MatDialog](https://material.angular.io/components/dialog/overview).
   *
   * - After opening, a MatDialogReference is returned, see documentation.
   * - After closing, an action is dispatched to `afterClosed()`:
   *  - *cancel*: When clicking the 'cancel'-button;
   *  - *action*: When the action button is clicked;
   *  - *undefined*: When the backdrop is clicked, but can be tracked by using backdropClick().
   *
   * *Note: All observable methods are automatically unsubscribed after one take.*
   *
   * @param config Modal configuration.
   * @returns Dialog reference as a MatDialogRef.
   */
  public confirm(config: ModalConfig): MatDialogRef<ModalComponent> {
    return this.matDialog.open(ModalComponent, {
      data: { ...this.DEFAULT_CONFIRM_CONFIG, ...this._parseActions({ ...this.DEFAULT_CONFIRM_CONFIG, ...config}) }
    });
  }

  /**
   * Close all open modals.
   */
  public closeAll(): void {
    this.matDialog.closeAll();
  }

  /**
   * Check whether there are currently any open modals.
   */
  public isOpen(): boolean {
    return this.matDialog.openDialogs.length > 0;
  }

  /**
   * Open modal with a custom component.
   *
   * The modal is a wrapper for [Material's MatDialog](https://material.angular.io/components/dialog/overview).
   *
   * - After opening, a MatDialogReference is returned, see documentation.
   * - After closing, an action is dispatched to `afterClosed()`:
   *
   * *Note: All observable methods are automatically unsubscribed after one take.*
   *
   * To retrieve the DialogReference in the modal, use in constructor():
   * - `public dialogRef: MatDialogRef<MyComponent>`
   *
   * To retrieve the data in the component, use in constructor():
   * - `@Inject(MAT_DIALOG_DATA) public readonly data: any`
   *
   * @param component A component to pass to the modal.
   * @param data A data object to be passed to the the modal.
   * @returns Dialog reference as a MatDialogRef.
   */
  public open(component: any, data?: any): MatDialogRef<any> {
    return this.matDialog.open(component, { data: data });
  }

  /**
   * Open add area modal.
   *
   * @param cluster Parent cluster of the area.
   * @returns Dialog reference as a MatDialogRef.
   */
  public openAddArea(template: Partial<Area>): MatDialogRef<AreaAddComponent> {
    return this.matDialog.open(AreaAddComponent, { data: { template: template }});
  }

  /**
   * Open add cluster modal.
   *
   * @param building Parent building of the cluster.
   * @returns Dialog reference as a MatDialogRef.
   */
  public openAddCluster(building: Building): MatDialogRef<ClusterAddComponent> {
    return this.matDialog.open(ClusterAddComponent, { data: { building: building }});
  }

  /**
   * Open assign to modal.
   *
   * @param element The element to open the assign to modal for.
   * @param preset The preset id.
   * @returns Dialog reference as a MatDialogRef.
   */
  public openAssignTo(element: ProjectElement, preset: ElementAssignToOptions): MatDialogRef<ModalAssignToComponent> {
    return this.matDialog.open(ModalAssignToComponent, { data: { element: element, preset: preset }});
  }

  /**
   * Open edit cluster.
   *
   * @param cluster The cluster to edit.
   * @returns Dialog reference as a MatDialogRef.
   */
  public openEditCluster(cluster: Cluster): MatDialogRef<ClusterEditComponent> {
    return this.matDialog.open(ClusterEditComponent, { data: { cluster: cluster }});
  }

  /**
   * Open filters menu dialog.
   *
   * @todo Interface and structure for tree object.
   *
   * @param tree Nested object for rendering the tree view.
   * @returns Dialog reference as a MatDialogRef.
   */
  public openFilters(tree: FilterTreeJSON): MatDialogRef<ModalFiltersComponent> {
    return this.matDialog.open(ModalFiltersComponent, { data: tree });
  }

  /**
   * Add new paragraph to document.
   *
   * Gives a choice between an enmpty paragraph, and importing an existing one from the
   * template library.
   *
   * @param submodule The parent submodule.
   */
  public openParagraphAdd(submodule: ProjectSubmodule): MatDialogRef<ModalParagraphAddComponent> {
    return this.matDialog.open(ModalParagraphAddComponent, { data: { submodule: submodule }, panelClass: 'dialog-paragraph-add' });
  }

  /**
   * Restore paragraph text.
   *
   * @param element The element to restore the value for.
   * @returns Dialog referenceas a MatDialogRef.
   */
  public openRestoreParagraph(element: ProjectElement): MatDialogRef<ModalRestoreParagraphComponent> {
    return this.matDialog.open(ModalRestoreParagraphComponent, { data: { element: element } });
  }

  /*
    * Open modal with a TinyMCE editor.
   *
   * @param element Project element to edit.
   * @returns Dialog reference.
   */
  public openTinymce(element: ProjectElement): MatDialogRef<ModalTinyMCEComponent> {
    return this.matDialog.open(ModalTinyMCEComponent, { data: { element: element }, panelClass: 'dialog-tinymce' });
  }

  /**
   * Open a prompt modal.
   *
   * The prompt modal contains one editable area to either set or update a property.
   *
   * @param title The title to display.
   * @param value The value to set or edit. Can be empty when set.
   * @param textarea Whether the input should be a textarea. Defaults to single line input.
   * @returns Dialog reference as a MatDialogRef, with the (new) value as param.
   */
  public prompt(title: string, value?: string | null, textarea?: boolean): MatDialogRef<ModalPromptComponent> {
    return this.matDialog.open(ModalPromptComponent, { data: { value: value, title: title, textarea: textarea }});
  }

  /**
   * Makes sure `action` is always in array format.
   *
   * @todo Patch by default.
   *
   * @param config Modal config.
   * @returns The patched modal config.
   */
  private _parseActions(config: ModalConfig): ModalConfig {
    if (config.action && !config.actions) {
      config.actions = [{ label: config.action, action: config.action, icon: config.actionIcon }];
    }
    return config;
  }
}
